import { match } from 'ts-pattern';
import type { CMSBanner as CMSBannerType } from '../schemas';
import { CMSColumns } from '../cms-columns';
import { CMSTextAdvanced } from '../cms-text-advanced';
import { CMSText } from '../cms-text';
import { getBannerThemeStyles } from './transforms';
import { cn } from '@/lib/utils';
import { CMSTabs } from '../cms-tabs';
import { usePage } from '@/hooks/use-page';

interface CMSBannerProps {
  bannerData: CMSBannerType;
  className?: string;
  startingPosition: number;
}
const CMSBanner = ({
  bannerData,
  className,
  startingPosition,
}: CMSBannerProps) => {
  const themeColour = getBannerThemeStyles(bannerData.theme.key);
  const pageKey = usePage();

  return (
    <div
      className={cn(themeColour, 'py-[0.1px]', className)}
      data-component="CMSBanner"
    >
      {bannerData.content.map((contentItem, index) =>
        match(contentItem)
          .with({ type: 'rich_text' }, (item) => (
            <div key={item.id}>CMSRichText</div>
          ))
          .with({ type: 'text' }, (textData) => (
            <CMSText key={index} textData={textData} />
          ))
          .with({ type: 'text_advanced' }, (textAdvancedData) => {
            if (bannerData.background_image?.url) {
              return (
                <div
                  key={index}
                  className={cn(
                    `container lg:pr-[82px] flex flex-col pt-5 items-center md:flex-row gap-6 md:gap-4 md:pt-[50px] lg:gap-4 lg:pt-[74px]`,

                    // Margin for p tag under h1 tag in banner with image
                    '[&_[data-component="CMSTextAdvanced"]_[data-component="CMSText"]_p]:mt-4 md:[&_[data-component="CMSTextAdvanced"]_[data-component="CMSText"]_p]:mt-5 lg:[&_[data-component="CMSTextAdvanced"]_[data-component="CMSText"]_p]:mt-6',

                    //Margin for Address search component in banner with image
                    `[&_[data-component="CMSTextAdvanced"]_[data-component="AddressSearch"]]:mt-4
                     md:[&_[data-component="CMSTextAdvanced"]_[data-component="AddressSearch"]]:mt-6
                     lg:[&_[data-component="CMSTextAdvanced"]_[data-component="AddressSearch"]]:mt-10
                    `,

                    // font weight under address search component (e.g Trusted by customer) small tag
                    `[&_[data-component="CMSTextAdvanced"]_[data-component="CMSText"]_p_small]:font-light`,
                    // text size under address search component (e.g Trusted by customer) small tag
                    `[&_[data-component="CMSTextAdvanced"]_[data-component="CMSText"]_p_small]:text-xxs
                     md:[&_[data-component="CMSTextAdvanced"]_[data-component="CMSText"]_p_small]:text-xs
                     lg:[&_[data-component="CMSTextAdvanced"]_[data-component="CMSText"]_p_small]:text-sm`,

                    // font weight under address search component (e.g Trusted by customer) small tag inside CMSInlineTextImage
                    `[&_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]_.richText_small]:font-nunito300`,

                    // text size under address search component (e.g Trusted by customer) small tag inside CMSInlineTextImage
                    `[&_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]_.richText_small]:text-xxs
                      md:[&_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]_.richText_small]:text-xs
                      lg:[&_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]_.richText_small]:text-sm`,

                    //Add margin top for CMSInlineTextImage (e.g Trusted by customer)
                    `[&_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]]:mt-4
                      md:[&_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]]:mt-6
                      lg:[&_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]]:mt-[50px]`,

                    // Add padding bottom if it has no CMSColumnsCarousel sibling
                    '[&:not(:has(+[data-component="CMSColumnsCarousel"]))]:pb-[50px] lg:[&:not(:has(+[data-component="CMSColumnsCarousel"]))]:pb-[80px]'
                  )}
                  data-component="CMSBannerWithImageContainer"
                >
                  <CMSTextAdvanced
                    key={textAdvancedData.id}
                    textAdvancedData={textAdvancedData}
                    className={'md:w-[344px] lg:w-[556px]'}
                    isFromBanner
                  />

                  <div className="relative shrink-0 md:w-[344px] lg:w-[556px]">
                    <img
                      className="w-full"
                      alt={bannerData.background_image?.alt ?? ''}
                      src={bannerData.background_image.url}
                      loading="lazy"
                    />
                  </div>
                </div>
              );
            }
            return (
              <div
                key={textAdvancedData.id}
                className={cn(
                  'container',

                  // Add padding bottom if it has no CMSColumnsCarousel sibling
                  '[&:not(:has(+:is([data-component="CMSColumnsCarousel"],[data-component="CMSColumnsStandard"]))):last-child]:pb-[50px] lg:[&:not(:has(+:is([data-component="CMSColumnsCarousel"],[data-component="CMSColumnsStandard"]))):last-child]:pb-[72px]',
                  // /* Adds top padding in mobile breakpoint to all direct children with data-component="CMSTextAdvanced",
                  // except when they contain a trustpilot section. */
                  '[&_[data-component="CMSTextAdvanced"]:not(:has(.trustpilot-section))]:pt-8 md:[&_[data-component="CMSTextAdvanced"]:not(:has(.trustpilot-section))]:pt-12 lg:[&_[data-component="CMSTextAdvanced"]:not(:has(.trustpilot-section))]:pt-20',

                  // font weight under address search component (e.g Trusted by customer) small tag inside CMSInlineTextImage
                  `[&_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]_.richText_small]:font-light`,

                  // text size under address search component (e.g Trusted by customer) small tag inside CMSInlineTextImage
                  `[&_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]_.richText_small]:text-xxs
                    md:[&_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]_.richText_small]:text-xs
                    lg:[&_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]_.richText_small]:text-sm`,

                  //Add margin top for CMSInlineTextImage (e.g Trusted by customer)
                  `[&_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]]:mt-4
                   md:[&_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]]:mt-6
                   lg:[&_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]]:mt-[50px]`,

                  // /* Adds bottom padding in mobile breakpoint to CMSTextAdvanced components, but only when they contain
                  // an AddressSearch component inside them. */
                  '[&_[data-component="CMSTextAdvanced"]_[data-component="AddressSearch"]]:mt-6',
                  'md:[&_[data-component="CMSTextAdvanced"]_[data-component="AddressSearch"]]:mt-8',

                  //Add margin top and margin top & bottom image CMSText inside banner
                  '[&_[data-component="CMSText"]_img]:my-8',

                  // Remove padding top for img if it's not the first child inside h1,h2,h3 & p tags
                  '[&_[data-component="CMSText"]_h1:first-child_img:nth-child(1)]:mt-0',
                  '[&_[data-component="CMSText"]_h2:first-child_img:nth-child(1)]:mt-0',
                  '[&_[data-component="CMSText"]_h3:first-child_img:nth-child(1)]:mt-0',
                  '[&_[data-component="CMSText"]_p:first-child_img:nth-child(1)]:mt-0',

                  // Add margin bottom for p tag
                  '[&_[data-component="CMSText"]_p]:my-4',

                  // Add margin top to h2 tag itself if it is not first child inside CMSColumnsStandard
                  `[&_[data-component="CMSTextAdvanced"]_[data-component="CMSText"]_h2:not(:first-child)]:mt-8 
                   md:[&_[data-component="CMSTextAdvanced"]_[data-component="CMSText"]_h2:not(:first-child)]:mt-12 
                   lg:[&_[data-component="CMSTextAdvanced"]_[data-component="CMSText"]_h2:not(:first-child)]:mt-20`,

                  // Add margin top to h3 tag itself if it is not first child inside CMSColumnsStandard
                  `[&_[data-component="CMSTextAdvanced"]_[data-component="CMSText"]_h3:not(:first-child)]:mt-6 
                   md:[&_[data-component="CMSTextAdvanced"]_[data-component="CMSText"]_h3:not(:first-child)]:mt-8`,
                  {
                    // if it's in speed test page remove padding top for rich text advanced
                    '[&:last-child_[data-component="CMSTextAdvanced"]:not(:has(.trustpilot-section))]:pt-0 md:[&:last-child_[data-component="CMSTextAdvanced"]:not(:has(.trustpilot-section))]:pt-0 lg:[&:last-child_[data-component="CMSTextAdvanced"]:not(:has(.trustpilot-section))]:pt-0':
                      pageKey &&
                      pageKey === 'speed-test' &&
                      startingPosition !== 0,

                    // if it's in speed test page add margin bottom for HR Tag
                    '[&_[data-component="CMSTextAdvanced"]_hr]:mb-6 md:[&_[data-component="CMSTextAdvanced"]_hr]:mb-12':
                      pageKey && pageKey === 'speed-test',

                    // Remove margin bottom for inline text image if it's the last element.
                    '[&_[data-component="CMSInlineTextImage"]:last-child]:mb-0':
                      startingPosition === 0,
                  }
                )}
              >
                <CMSTextAdvanced
                  className={cn('w-full', {
                    // if index is 0 it means it's a header banner, apply the header component with no image max three column width.
                    'md:w-[520px] lg:w-[780px]':
                      startingPosition === 0 &&
                      pageKey &&
                      !['app', 'download'].includes(pageKey),
                  })}
                  textAdvancedData={textAdvancedData}
                  isFromBanner
                />
              </div>
            );
          })
          .with({ type: 'columns' }, (columnsData) => (
            <CMSColumns
              className={cn(
                '[&[data-component="CMSColumnsStandard"]]:container',

                //Add margin bottom for CMSColumnsStandard if it's the last child
                '[&[data-component="CMSColumnsStandard"]:last-child]:mb-[50px] lg:[&[data-component="CMSColumnsStandard"]:last-child]:mb-[80px]',

                //Set CMSTextAdvanced component to w-full for CMSColumnsStandard
                '[&[data-component="CMSColumnsStandard"]_[data-component="CMSTextAdvanced"]]:w-full',

                //Add text styling on text within InlineTextImage
                `[&[data-component="CMSColumnsStandard"]_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]_.richText]:text-base 
                 lg:[&[data-component="CMSColumnsStandard"]_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]_.richText]:text-lg`,

                // Add margin bottom for p tag inside CMSColumnsStandard
                '[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_p]:mb-4',
                {
                  //Reduce padding top for cms banner inside header(first banner), For example: in home page
                  '[&[data-component="CMSColumnsCarousel"]]:pt-0 md:[&[data-component="CMSColumnsCarousel"]]:pt-0 lg:[&[data-component="CMSColumnsCarousel"]]:pt-0':
                    startingPosition === 0,
                  '[&[data-component="CMSColumnsCarousel"]]:mt-4 md:[&[data-component="CMSColumnsCarousel"]]:mt-10 lg:[&[data-component="CMSColumnsCarousel"]]:mt-10':
                    startingPosition === 0,
                }
              )}
              isFromBannerWithImage={!!bannerData.background_image?.url}
              key={columnsData.id}
              columnsData={columnsData}
            />
          ))
          .with({ type: 'tabs' } as const, (tabsData) => (
            <CMSTabs
              key={index}
              tabsData={tabsData}
              className="cms-render container bg-transparent mt-6 md:mt-8 lg:mt-16"
            />
          ))
          .otherwise(() => null)
      )}
    </div>
  );
};
export { CMSBanner };
